<template>
  <base-tab :title="title" :sub-title="subTitle" class="tab__evaluation-assessment">
    <div v-if="loading" class="d-flex justify-content-center p-4">
      <b-spinner label="Loading..." variant="dark" />
    </div>

    <b-row v-else class="mb-5">
      <b-col md="10" lg="8" offset-md="1" offset-lg="2">
        <b-col v-if="learningUnit && learningUnit.score_description" cols="12" class="px-0">
          <learning-unit-score-description-card
            :learning-unit="learningUnit"
            :disabled="disabled"
            :editable="!disabled"
          />
        </b-col>

        <b-col
          v-else-if="!disabled"
          cols="12"
          class="text-right button__add-score-description-wrpper p-0 mt-4"
        >
          <router-link :to="scoreDescriptionPage">
            <b-button
              :ref="`button__add-score-description`"
              class="button__add-score-description"
              variant="outline-primary"
            >
              Add Overall Passing Criteria
            </b-button>
          </router-link>
        </b-col>

        <post-evaluation-assessment-part
          v-for="(partMeta, index) in partMetaData"
          :key="`card__part-${index + 1}-task-list`"
          class="my-4"
          :part="partMeta"
          :learning-unit="learningUnit"
          :post-ids="partPostIds[partMeta] ? partPostIds[partMeta] : []"
          :addable="canAddTask(partMeta) && !disabled"
          :editable="!disabled"
          :has-user-enrolled="hasUserEnrolled"
        >
        </post-evaluation-assessment-part>
      </b-col>
    </b-row>
  </base-tab>
</template>

<script>
import { usePostExtractor } from '@/composables/usePostExtractor';
import LearningUnit from '@/models/LearningUnit';
import Post from '@/models/Post';
import BaseTab from '@/components/base-components/BaseTab';
import PostEvaluationAssessmentPart from '@/components/Post/PostEvaluationAssessmentPart';
import LearningUnitScoreDescriptionCard from '@/components/LearningUnit/LearningUnitScoreDescriptionCard';
import Application from '@/models/Application';

export default {
  components: {
    BaseTab,
    PostEvaluationAssessmentPart,
    LearningUnitScoreDescriptionCard
  },
  props: {
    learningUnit: {
      type: LearningUnit,
      required: true
    },

    disabled: {
      type: [Number, Boolean],
      default: false
    },

    active: {
      type: [Number, Boolean],
      default: false
    }
  },

  data() {
    return {
      title: 'Evaluation assessment',
      subTitle:
        'Candidates for this certification should be able to verify specific competency based on their experiences, abilities and skills.',
      loading: false,
      hasUserEnrolled: false
    };
  },

  computed: {
    partMetaData() {
      return Object.keys(Post.PART);
    },

    postEvaluationAssessments() {
      const posts = Post.query()
        .where('post_type', 'EVALUATION_ASSESSMENT')
        .where('learning_unit_id', this.learningUnit.id)
        .get();

      return posts;
    },

    partPostIds() {
      const { extractPostIdGroupByPart } = usePostExtractor();

      const { postEvaluationAssessments = [] } = this;

      return postEvaluationAssessments.length
        ? extractPostIdGroupByPart(postEvaluationAssessments)
        : {};
    },

    scoreDescriptionPage() {
      return {
        name: 'learning-unit/score-description',
        params: {
          learningUnitId: this.learningUnit.id
        },
        query: {
          previousPath: this.$route.fullPath
        }
      };
    }
  },

  watch: {
    active: {
      immediate: true,
      handler: 'onActive'
    }
  },

  methods: {
    canAddTask(partMeta) {
      let canAddTask = true;

      const partLimit = Post.PART_LIMIT[partMeta];

      if (partLimit) {
        const { partPostIds = {} } = this;

        const numberPosts = partPostIds[partMeta] ? partPostIds[partMeta].length : 0;

        canAddTask = numberPosts < partLimit;
      }

      return canAddTask;
    },

    onActive(isActive) {
      if (isActive) {
        this.fetch();
      }
    },

    async fetch() {
      const { id: learningUnitId } = this.learningUnit;

      this.loading = true;

      try {
        await Post.api().fetchEvaluationAssessments({
          learningUnitId
        });
        const {
          response: { data = 0 }
        } = await Application.api().count({ learning_unit: learningUnitId });
        this.hasUserEnrolled = data ? true : false;
      } catch (error) {
        this.$toast.error(`Cannot fetch evaluation assessments, please try again.`);
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.button__add-score-description-wrpper {
  max-width: 910px;
  margin: auto;
}
</style>
